'use strict'
import React, { useState, useCallback } from 'react'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { secondmentType } from '../../../../_constants/Code'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { getMessage } from '../../../../common/utils/MessageUtils'

/*==================================
 定数定義
 ==================================*/
// conditionを選択肢用に配列化
const conditionOptions = Object.entries(secondmentType).map(([key, { name, value }]) => ({
  label: name,
  value: value
}))

/*==================================
 カスタムフック
 ==================================*/
/**
 * 選択された年度ごとに、各指標に対する条件の選択状態を管理するカスタムフック。
 *
 * @param {string | number} selectedFiscalYear - 現在選択中の年度
 * @returns {{
 *   selectedCondition: Record<string, string> // 指標コードをキーとした条件選択値のマップ
 *   handleChangeCondition: (indicatorKey: string) => (e: React.ChangeEvent<HTMLSelectElement>) => void // 指標ごとの条件変更ハンドラ
 * }}
 */
export const useIndicatorConditionSelection = (selectedFiscalYear) => {
  /** 状態構造例： { 2023: { 'Index-001': 'secondingCompany', 'Index-002': 'receivingCompany', ... }, 2024: { ... } } */
  const [selectedConditionsByYear, setSelectedConditionsByYear] = useState({})

  // 現在選択年度の指標ごとの条件選択値を取得（なければ空オブジェクト）
  const selectedCondition = selectedConditionsByYear[selectedFiscalYear] || {}

  /**
   * 指標ごとの条件選択値変更ハンドラを生成する関数
   *
   * @param {string} indicatorKey - 指標のコード（キー）
   * @returns {function} - イベントハンドラ（React.ChangeEvent<HTMLSelectElement>を受け取る）
   */
  const handleChangeCondition = useCallback(
    (indicatorKey) => (e) => {
      const selectedValue = e.target.value
      setSelectedConditionsByYear((prev) => {
        const prevForYear = prev[selectedFiscalYear] || {}
        return {
          ...prev,
          [selectedFiscalYear]: {
            ...prevForYear,
            [indicatorKey]: selectedValue
          }
        }
      })
    },
    [selectedFiscalYear]
  )

  return {
    selectedCondition,
    selectedConditionsByYear,
    setSelectedConditionsByYear,
    handleChangeCondition
  }
}

/*==================================
    コンポーネント（JSX）
  ===================================*/
/**
 * 指標ごとの条件設定を表示するテーブルコンポーネント。
 *
 * @param {Object} props
 * @param {Object} props.indicatorInfo - 指標情報オブジェクト。`Config`プロパティに指標配列を持つ。
 * @param {Record<string, string>} props.selectedCondition - 各指標の選択中の条件値マップ
 * @param {(indicatorKey: string) => (e: React.ChangeEvent<HTMLSelectElement>) => void} props.handleChangeCondition -
 *   指標ごとの条件変更イベントハンドラ
 * @param {Object} [props.inputError={}] - 入力エラー情報（indicatorInfoキーにエラー有無）. Default is `{}`
 * @returns {React.ReactElement}
 */
const IndicatorConditionSettingTable = React.memo(
  ({ indicatorInfo, selectedCondition, handleChangeCondition, inputError = {} }) => {
    const SelectClassList = ['w-full']
    const SelectClasses = SelectClassList.join(' ')

    if (indicatorInfo.Config.length === 0) {
      return (
        <div className="text-center my-10">
          <span className="text-h5 red--text">指標が未登録です。</span>
        </div>
      )
    }

    const hasError = !!Object.keys(inputError?.indicatorInfo || {}).length

    return (
      <div className="setting-table min-w-[100px] max-w-[50%]">
        <table className="base-table table-fixed w-full">
          <colgroup>
            <col className="w-[20%]" />
            <col className="w-[20%]" />
          </colgroup>
          <thead>
            <tr>
              <th>指標</th>
              <th>条件</th>
            </tr>
          </thead>
          <tbody className="text-caption-2 !text-base font-medium">
            {indicatorInfo.Config.map(({ Code, Value }, idx) => {
              // 現在の条件選択値を取得（未選択なら空文字）
              const value = selectedCondition[Code] || ''

              return (
                <tr key={Code} data-testid={`indicator-row-${Code}`}>
                  <td>
                    <CustomTooltip title={Value} useParentWidth={true}>
                      {(ref) => (
                        <div ref={ref} className="truncate font-normal">
                          <span>{Value}</span>
                        </div>
                      )}
                    </CustomTooltip>
                  </td>
                  <td className="form-control !py-2 relative">
                    <ChrofyCustomSelect
                      id={`condition-select-${Code}`}
                      name={`condition_${Code}`}
                      value={value}
                      onChange={handleChangeCondition(Code)}
                      className={SelectClasses}
                      options={conditionOptions}
                      disabled={false}
                      isError={inputError?.indicatorInfo?.[Code]}
                      hasNoSelect={false}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {hasError && <p className="red--text">{getMessage('E010001', '条件')}</p>}
      </div>
    )
  }
)

/**
 * 出向者の取り扱い設定セクションを表示・管理するコンポーネント。
 *
 * @param {Object} props
 * @param {Object} props.indicatorInfo - 指標情報オブジェクト
 * @param {string | number} props.selectedFiscalYear - 選択中の年度
 * @param {Record<string, string>} props.selectedCondition - 各指標の選択中の条件値マップ
 * @param {(indicatorKey: string) => (e: React.ChangeEvent<HTMLSelectElement>) => void} props.handleChangeCondition -
 *   指標ごとの条件変更イベントハンドラ
 * @param {Object} [props.inputError] - 入力エラー情報
 * @returns {React.ReactElement}
 */
const SecondedEmployeeHandlingSettings = ({ indicatorInfo, selectedCondition, handleChangeCondition, inputError }) => {
  // バリデーションエラー初期化
  const clientErrors = {}

  return (
    <>
      <div className="text-body-2-bold grow mb-2">■出向者の取り扱い設定</div>
      <div className="flex items-center justify-between mb-6">
        各指標の計算において、出向者をどのように取り扱うかを設定してください。
      </div>
      <IndicatorConditionSettingTable
        indicatorInfo={indicatorInfo}
        selectedCondition={selectedCondition}
        handleChangeCondition={handleChangeCondition}
        inputError={inputError}
      />
    </>
  )
}

export default SecondedEmployeeHandlingSettings
